import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryResult
} from "@tanstack/react-query";
import { flatten } from "lodash";
import { useEffect, useRef } from "react";

import { TimeEnum } from "../../../lib/enums";
import { CANCEL_ERROR } from "../../../requests";
import { ErrorResponse, MessageErrorResponse } from "../../../requests/@types";
import { CloudSearchResponse } from "../../../services/@types";

import { advancedSearchKeys } from "../../samples/queryKeys";
import { SearchProps } from "../@types";
import api from "../api";

import { LOCATION } from "../consts";
import { SearchTypeEnum } from "../enums";
import {
  constructQuery,
  constructSelections,
  createFilterChangeSelections,
  determineSort
} from "../util";
import { getFetchPeriod } from "../util/cloudtotalcount";
import { DEFAULT_PAGE, queryOptions } from "./consts";

export const useCloudSearch = (props: SearchProps) => {
  const {
    args: { params },
    options
  } = props;

  const abortControllerRef = useRef<AbortController | null>(null);

  const { q, selections, page, pagesize, sort, location, isRecent } =
    params || {};
  const isPublicCloudTab = location === LOCATION.CLOUD;
  const reqPage = isPublicCloudTab ? page : DEFAULT_PAGE;
  const reqPageSize = isPublicCloudTab ? pagesize : 0;

  const { period_type: { value = "" } = {} } = selections || {};
  const { start_search_date, end_search_date } = getFetchPeriod(
    value === TimeEnum.ALL || value.includes("*") ? TimeEnum.LAST_MONTH : value
  );

  const cloudSelections = createFilterChangeSelections(
    LOCATION.CLOUD,
    selections
  );

  const infiniteQueryKey: unknown[] = advancedSearchKeys.fetchCloudDataKey({
    q,
    selections: cloudSelections,
    page: reqPage,
    pagesize: reqPageSize,
    sort,
    location,
    start_search_date,
    end_search_date
  });

  const search = (
    endSearchDate: string | null
  ): Promise<CloudSearchResponse> => {
    abortControllerRef.current?.abort("Aborting previous call.");
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const searchType =
      SearchTypeEnum[
        (selections?.cloud_search_type?.value ||
          "") as keyof typeof SearchTypeEnum
      ];
    const query = constructQuery(q || "", searchType);

    return api
      .retrieveAdvancedSearchSamples(
        {
          q: query,
          location: LOCATION.CLOUD,
          page,
          pagesize,
          sort: determineSort(LOCATION.CLOUD, sort),
          ...(location === LOCATION.CLOUD && isRecent ? { recent: true } : {}),
          ...constructSelections(cloudSelections),
          ...(searchType ? { shareability_filter: true } : {}),
          ...(start_search_date != null && { start_search_date }),
          ...(endSearchDate != null && { end_search_date: endSearchDate })
        },
        abortController.signal
      )
      .then(
        ({ data: responseData }) => responseData
      ) as Promise<CloudSearchResponse>;
  };

  const cloudQuery = useInfiniteQuery<
    CloudSearchResponse,
    ErrorResponse,
    CloudSearchResponse & InfiniteData<CloudSearchResponse>,
    unknown[],
    string | null
  >({
    ...queryOptions,
    enabled: Boolean(options.enabled),
    initialPageParam: end_search_date,
    queryKey: infiniteQueryKey,
    queryFn: ({ pageParam = null }) => search(pageParam),
    getNextPageParam: lastPage => lastPage.last_search_date ?? undefined,
    select: ({ pages, pageParams }) => {
      const total = pages.reduce(
        (acc, curr) => acc + curr.cloud_sample_count ?? 0,
        0
      );
      const items = pages.map(infinitePage => infinitePage.data);
      const firstResponse = pages.at(-1);
      return {
        ...firstResponse,
        cloud_sample_count: total,
        data: flatten(items).slice(0, pagesize),
        pageParams,
        pages
      } as CloudSearchResponse & InfiniteData<CloudSearchResponse>;
    }
  });

  const { hasNextPage, isFetchingNextPage, data, error, fetchNextPage } =
    cloudQuery;

  const pageNotFilled = (data?.data?.length ?? 0) < pagesize;
  const isCancelledQuery = error?.problem === CANCEL_ERROR;
  const shouldFetchNextPage =
    hasNextPage && !isFetchingNextPage && pageNotFilled && !isCancelledQuery;

  useEffect(() => {
    if (shouldFetchNextPage) {
      fetchNextPage();
    }
  }, [shouldFetchNextPage, fetchNextPage]);

  useEffect(() => {
    // successful request that is not refetching
    if (cloudQuery.status === "success" && cloudQuery.fetchStatus === "idle") {
      if (options.onSuccess)
        options.onSuccess(
          data as unknown as CloudSearchResponse &
            InfiniteData<CloudSearchResponse>
        );
    }

    if (cloudQuery.status === "error" && options.onError)
      options.onError(
        cloudQuery.error as unknown as ErrorResponse<MessageErrorResponse>
      );
  }, [cloudQuery.status, cloudQuery.fetchStatus]);

  return {
    cloudQuery: cloudQuery as UseInfiniteQueryResult<
      CloudSearchResponse & InfiniteData<CloudSearchResponse>,
      ErrorResponse<MessageErrorResponse>
    >,
    cloudAbortController: abortControllerRef.current
  };
};
